import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ConfirmModal, StateView } from '@/card/record'
import { Icon, type IconName } from '@/components/Icon'
import {
  absorbAdvances,
  convertAll,
  convertLine,
  fetchBankAccounts,
  fetchNeedingAttention,
  fetchUnprocessedLines,
  importStatement,
} from './api'
import {
  amountOf,
  dateText,
  Failure,
  Money,
  OrganizationPicker,
  OrganizationRequired,
  OrganizationsFailure,
  PaymentsPage,
  QueryFailure,
  TableSkeleton,
  useOrganizations,
} from './PaymentsShell'
import { StatementImports } from './StatementImports'
import type { ConversionResult, ImportResult } from './types'

/**
 * Сверка: загрузка выписки, проведение, разбор нераспознанного (ТЗ §4.4) — тикет #78,
 * вёрстка по BankRecon.dc.html.
 *
 * <h2>Почему один экран, а не три</h2>
 *
 * Это один рабочий процесс бухгалтера, а не три раздела: загрузил файл — провёл строки —
 * разобрал то, что не легло само. Разложив это по трём пунктам меню, мы бы получили
 * третий шаг, на который никто не заходит: экран, куда надо помнить сходить, — это экран,
 * куда не ходят, а нераспознанный платёж существует ровно до тех пор, пока о нём помнят.
 *
 * <h2>Одна таблица, две очереди</h2>
 *
 * В таблице — остаток работы, а не вся выписка:
 *
 * - **строки выписки** «Не проведено» — банк сказал, мы не провели;
 * - **платежи** «Требует решения» — провели, плательщика не опознали.
 *
 * Разнесённые строки сервер не отдаёт, поэтому статуса «Разнесено» из макета здесь нет:
 * рисовать его было бы нечем. Итог проведения показывает `stillUnprocessed` вместе с
 * числом успехов — «проведено 12» без остатка читается как «готово».
 *
 * <h2>История загрузок</h2>
 *
 * Под очередью — загрузки выписки по счёту: когда, какой файл, сколько строк новых и
 * сколько повторов, кто загрузил. Повторная загрузка называет ту, что принесла строки
 * первой (`duplicateOfImportId`), и кнопка «Открыть ту загрузку» ведёт к ней — оператор не
 * гадает по датам, когда это уже было.
 */
type Filter = 'all' | 'lines' | 'attention'

interface QueueRow {
  key: string
  kind: 'line' | 'payment'
  date: string
  number: string | null
  name: string | null
  inn: string | null
  purpose: string | null
  direction: 'incoming' | 'outgoing'
  amount: string
  currency: string
  unallocated: string | null
  id: string
}

export function ReconciliationPage() {
  const { t, user } = useApp()
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [accountId, setAccountId] = useState<string | null>(null)
  const [importOutcome, setImportOutcome] = useState<{ result: ImportResult; file: string; at: string } | null>(null)
  const [conversion, setConversion] = useState<ConversionResult | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [openedImport, setOpenedImport] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Загрузка и проведение — одно право (`payments.statement:create`). Без него кнопок нет:
  // кнопка, ведущая в гарантированный 403, учит игнорировать отказы.
  const mayPost = can(user, 'payments.statement', 'create')
  const mayOpenPayment = can(user, 'payments.payment', 'read')
  const mayReadStatements = can(user, 'payments.statement', 'read')

  const organizations = useOrganizations()

  const accounts = useQuery({
    queryKey: ['payment-bank-accounts', organizationId],
    queryFn: ({ signal }) => fetchBankAccounts(organizationId!, signal),
    enabled: Boolean(organizationId),
    retry: false,
  })

  // Счёт выбирается сам, только когда он один: выбор из нескольких — решение бухгалтера,
  // и подставленный за него первый попавшийся означал бы выписку, загруженную не туда.
  const selectedAccount = useMemo(() => {
    if (accountId) return accountId
    const list = accounts.data ?? []
    return list.length === 1 ? (list[0]?.id ?? null) : null
  }, [accountId, accounts.data])

  const lines = useQuery({
    queryKey: ['statement-lines', selectedAccount],
    queryFn: ({ signal }) => fetchUnprocessedLines(selectedAccount!, signal),
    enabled: Boolean(selectedAccount),
    retry: false,
  })

  const queue = useQuery({
    queryKey: ['payments-needing-attention', organizationId],
    queryFn: ({ signal }) => fetchNeedingAttention(organizationId!, signal),
    enabled: Boolean(organizationId),
    retry: false,
  })

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['statement-lines'] })
    void queryClient.invalidateQueries({ queryKey: ['payments-needing-attention'] })
  }

  const upload = useMutation({
    mutationFn: ({ content, file }: { content: string; file: string }) =>
      importStatement(selectedAccount!, content, file),
    onSuccess: (result, { file }) => {
      setImportOutcome({ result, file, at: new Date().toISOString() })
      setConversion(null)
      refresh()
      void queryClient.invalidateQueries({ queryKey: ['statement-imports'] })
    },
  })

  const convertEverything = useMutation({
    mutationFn: () => convertAll(selectedAccount!),
    onSuccess: (result) => {
      setConversion(result)
      setConfirming(false)
      refresh()
    },
    onError: () => setConfirming(false),
  })

  const convertOne = useMutation({
    mutationFn: (lineId: string) => convertLine(lineId),
    onSuccess: refresh,
  })

  const absorb = useMutation({
    mutationFn: () => absorbAdvances(selectedAccount!),
    onSuccess: refresh,
  })

  function take(file: File | undefined) {
    if (!file || !selectedAccount) return
    // Файл читается целиком: выписка это сотни килобайт, и потоковая загрузка тут
    // усложнила бы код ради размера, которого не бывает.
    void readText(file).then((content) => upload.mutate({ content, file: file.name }))
  }

  function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    take(event.target.files?.[0])
    // Сброс значения — чтобы повторный выбор ТОГО ЖЕ файла снова дал событие. Повторная
    // загрузка выписки штатна (сервер её распознаёт), и запретить её интерфейсом значило
    // бы заставить оператора искать обход.
    event.target.value = ''
  }

  const lineRows: QueueRow[] = (lines.data ?? []).map((line) => ({
    key: `line-${line.id}`,
    kind: 'line',
    date: line.statementDate,
    number: null,
    name: line.counterpartyName,
    inn: line.counterpartyInn,
    purpose: line.purpose,
    direction: line.direction,
    amount: line.amount,
    currency: line.currency,
    unallocated: null,
    id: line.id,
  }))
  const paymentRows: QueueRow[] = (queue.data ?? []).map((payment) => ({
    key: `payment-${payment.id}`,
    kind: 'payment',
    date: payment.paymentDate,
    number: payment.number,
    name: payment.payerName,
    inn: payment.payerInn,
    purpose: payment.purpose,
    direction: payment.direction,
    amount: payment.amount,
    currency: payment.currency,
    unallocated: payment.unallocatedAmount,
    id: payment.id,
  }))
  const all = [...lineRows, ...paymentRows]
  const needle = search.trim().toLowerCase()
  const rows = all
    .filter((row) => filter === 'all' || (filter === 'lines' ? row.kind === 'line' : row.kind === 'payment'))
    .filter((row) =>
      !needle ||
      [row.name, row.inn, row.purpose, row.number, row.amount]
        .some((value) => value != null && String(value).toLowerCase().includes(needle)),
    )

  const pending = lineRows.length
  const loadingQueue = (Boolean(selectedAccount) && lines.isLoading) || queue.isLoading
  const queueErrors = [selectedAccount ? lines.error : null, queue.error].filter(Boolean)

  const actions = selectedAccount && mayPost ? (
    <>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => absorb.mutate()}
        disabled={absorb.isPending}
      >
        {t('payments.absorbAdvances')}
      </button>
      <button
        type="button"
        className="btn btn-primary"
        aria-haspopup="dialog"
        disabled={pending === 0 || lines.isLoading}
        // Проводить нечего — кнопка говорит почему, а не просто гаснет.
        title={pending === 0 ? t('payments.recon.nothingToPost') : undefined}
        onClick={() => setConfirming(true)}
      >
        <Icon name="check" />
        {t('payments.convertAll')} · {pending}
      </button>
    </>
  ) : null

  const head = (
    <>
      <OrganizationPicker
        value={organizationId}
        onChange={(id) => {
          setOrganizationId(id)
          setAccountId(null)
          setImportOutcome(null)
          setConversion(null)
          setOpenedImport(null)
        }}
      />
      {organizationId && (accounts.data ?? []).length > 1 && (
        <select
          className="input"
          aria-label={t('payments.account')}
          value={selectedAccount ?? ''}
          onChange={(event) => {
            setAccountId(event.target.value || null)
            setOpenedImport(null)
          }}
        >
          <option value="">{t('payments.account.choose')}</option>
          {(accounts.data ?? []).map((account) => (
            <option key={account.id} value={account.id}>
              {account.bankName} · {t('payments.recon.accountShort')} …{account.accountNumber.slice(-4)}
            </option>
          ))}
        </select>
      )}
    </>
  )

  return (
    <PaymentsPage titleKey="nav.reconciliation" head={head} actions={actions} leadKey="payments.reconciliation.hint">
      {organizations.error ? (
        <OrganizationsFailure error={organizations.error} onRetry={() => void organizations.refetch()} />
      ) : organizations.isLoading ? (
        <TableSkeleton columns={columnTitles(t)} />
      ) : !organizationId ? (
        <OrganizationRequired
          organizations={organizations.data ?? []}
          onPick={setOrganizationId}
          titleKey="payments.recon.noOrg.title"
          textKey="payments.recon.noOrg.text"
        />
      ) : (
        <>
          <AccountState
            loading={accounts.isLoading}
            error={accounts.error}
            onRetry={() => void accounts.refetch()}
            count={(accounts.data ?? []).length}
            selected={Boolean(selectedAccount)}
          />

          {selectedAccount && mayPost && (
            <Dropzone
              outcome={importOutcome}
              busy={upload.isPending}
              over={dragOver}
              onOver={setDragOver}
              onDropFile={take}
              onFile={onFile}
              onOpenOriginal={mayReadStatements ? setOpenedImport : undefined}
            />
          )}

          {upload.error ? <Failure error={upload.error} /> : null}
          {conversion ? <ConversionSummary result={conversion} /> : null}
          {convertEverything.error ? <Failure error={convertEverything.error} /> : null}
          {convertOne.error ? <Failure error={convertOne.error} /> : null}
          {absorb.error ? <Failure error={absorb.error} /> : null}

          {queueErrors.map((error, index) => (
            <QueryFailure key={index} error={error} compact />
          ))}

          {loadingQueue ? (
            <TableSkeleton columns={columnTitles(t)} />
          ) : all.length === 0 ? (
            queueErrors.length === 0 && (
              <StateView
                boxed
                kind="empty"
                title={t('payments.recon.empty.title')}
                description={
                  <>
                    {selectedAccount && <span>{t('payments.unprocessedLines.empty')}. </span>}
                    <span>{t('payments.needingAttention.empty')}</span>
                  </>
                }
              />
            )
          ) : (
            <>
              <div className="toolbar">
                <div className="row-wrap" role="group" aria-label={t('payments.recon.filter')} style={{ gap: 6 }}>
                  <Chip on={filter === 'all'} icon="lines" label={t('payments.recon.chip.all')} count={all.length}
                        onClick={() => setFilter('all')} />
                  <Chip on={filter === 'lines'} icon="clock" label={t('payments.recon.status.line')}
                        count={lineRows.length} onClick={() => setFilter('lines')} />
                  <Chip on={filter === 'attention'} icon="alert" label={t('payments.recon.status.attention')}
                        count={paymentRows.length} onClick={() => setFilter('attention')} />
                </div>
                <div className="vsep" />
                <div className="input-icon pay-search">
                  <Icon name="search" />
                  <label htmlFor="recon-search" className="sr-only">{t('payments.recon.search')}</label>
                  <input
                    id="recon-search"
                    className="input input-sm"
                    placeholder={t('payments.recon.search.placeholder')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
              </div>

              {rows.length === 0 ? (
                <StateView kind="empty" compact title={t('payments.recon.filtered.empty')} />
              ) : (
                <QueueTable
                  rows={rows}
                  filtered={filter !== 'all' || needle !== ''}
                  mayPost={mayPost}
                  mayOpenPayment={mayOpenPayment}
                  converting={convertOne.isPending}
                  onConvert={(id) => convertOne.mutate(id)}
                />
              )}
              <p className="t-sm muted">{t('payments.recon.footnote')}</p>
            </>
          )}

          {selectedAccount && mayReadStatements && (
            <StatementImports accountId={selectedAccount} openedId={openedImport} onOpen={setOpenedImport} />
          )}
        </>
      )}

      <ConfirmModal
        open={confirming}
        tone="primary"
        icon="check"
        title={t('payments.recon.postAll.title', { count: pending })}
        confirmLabel={t('payments.recon.postAll.confirm', { count: pending })}
        pending={convertEverything.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => convertEverything.mutate()}
      >
        <p>{t('payments.recon.postAll.text')}</p>
        {paymentRows.length > 0 && (
          <p className="strong fg">{t('payments.recon.postAll.untouched', { count: paymentRows.length })}</p>
        )}
      </ConfirmModal>
    </PaymentsPage>
  )
}

function columnTitles(t: (key: string) => string): string[] {
  return [
    t('payments.recon.col.date'),
    t('payments.recon.col.number'),
    t('payments.recon.col.counterparty'),
    t('payments.recon.col.purpose'),
    t('payments.recon.col.in'),
    t('payments.recon.col.out'),
    t('payments.recon.col.status'),
    '',
  ]
}

function Chip({ on, icon, label, count, onClick }: {
  on: boolean; icon: IconName; label: string; count: number; onClick: () => void
}) {
  return (
    <button type="button" className="chip" aria-pressed={on} onClick={onClick}>
      <Icon name={icon} />
      {label} <span className="count">{count}</span>
    </button>
  )
}

/** Счёт организации: загрузка, отказ, «не заведён», «выберите». */
function AccountState({ loading, error, onRetry, count, selected }: {
  loading: boolean; error: unknown; onRetry: () => void; count: number; selected: boolean
}) {
  const { t } = useApp()
  if (loading) return <span className="skel" style={{ height: 60, borderRadius: 8 }} aria-busy="true" />
  if (error) return <QueryFailure error={error} onRetry={onRetry} />
  // Без счёта выписку не с чем сопоставить — это настройка, а не пустота.
  if (count === 0) {
    return (
      <StateView
        boxed
        kind="setup"
        title={t('payments.recon.noAccount.title')}
        description={t('payments.recon.noAccount.text')}
      />
    )
  }
  if (!selected) {
    return (
      <div className="alert alert-neutral" role="status">
        <Icon name="building" />
        <span>{t('payments.recon.chooseAccount')}</span>
      </div>
    )
  }
  return null
}

/**
 * Зона загрузки и её итог.
 *
 * Повтор называется вслух (`.is-dup`), а не остаётся выводом из «загружено 0». Формат 1С не
 * несёт идентификатора транзакции, ключ собирается из номера, даты, суммы и счёта —
 * поэтому повторная загрузка того же файла это норма протокола, а не ошибка оператора.
 * Флаг `fullyDuplicate` приходит с сервера: клиент повтор не угадывает.
 */
function Dropzone({ outcome, busy, over, onOver, onDropFile, onFile, onOpenOriginal }: {
  outcome: { result: ImportResult; file: string; at: string } | null
  busy: boolean
  over: boolean
  onOver: (over: boolean) => void
  onDropFile: (file: File | undefined) => void
  onFile: (event: React.ChangeEvent<HTMLInputElement>) => void
  /** Нет — нет права читать историю загрузок; кнопки, ведущей в отказ, тогда нет. */
  onOpenOriginal?: (importId: string) => void
}) {
  const { t } = useApp()
  const input = (
    <input
      type="file"
      className="pay-dropzone-input"
      accept=".txt,text/plain"
      aria-label={t('payments.import.file')}
      onChange={onFile}
      disabled={busy}
    />
  )
  const dropHandlers = {
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault()
      onOver(true)
    },
    onDragLeave: () => onOver(false),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault()
      onOver(false)
      onDropFile(event.dataTransfer.files?.[0])
    },
  }

  if (outcome) {
    const { result, file, at } = outcome
    const time = new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    const duplicate = result.fullyDuplicate
    return (
      <div
        className={`dropzone pay-dropzone ${duplicate ? 'is-dup' : 'is-done'}`}
        role={duplicate ? 'alert' : 'status'}
        {...dropHandlers}
      >
        <Icon name={duplicate ? 'alert' : 'check'} />
        <span className="pay-dz-text">
          <span className="strong">
            {duplicate
              ? t('payments.import.duplicate', { total: result.linesInFile })
              : t('payments.import.done', {
                  imported: result.imported,
                  total: result.linesInFile,
                  duplicates: result.duplicates,
                })}
          </span>
          <span className="t-sm">
            <span className="mono">{file}</span> · {t('payments.import.at', { time })}
          </span>
        </span>
        {result.duplicates > 0 && result.duplicateOfImportId && onOpenOriginal && (
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => onOpenOriginal(result.duplicateOfImportId!)}>
            {t('payments.import.openOriginal')}
          </button>
        )}
        <label className="btn btn-ghost btn-sm">
          <Icon name="upload" size="sm" />
          {t('payments.import.more')}
          {input}
        </label>
      </div>
    )
  }

  return (
    <div
      className={`dropzone pay-dropzone${over ? ' is-over' : ''}`}
      role="group"
      aria-label={t('payments.import.zone')}
      aria-busy={busy}
      {...dropHandlers}
    >
      <Icon name="upload" />
      <span className="strong" style={{ color: 'var(--fg)' }}>
        {busy ? t('payments.import.busy') : t('payments.import.drop')}
      </span>
      <span className="t-sm">{t('payments.import.format')}</span>
      <label className="btn btn-secondary btn-sm">
        {t('payments.import.choose')}
        {input}
      </label>
    </div>
  )
}

function QueueTable({ rows, filtered, mayPost, mayOpenPayment, converting, onConvert }: {
  rows: QueueRow[]
  filtered: boolean
  mayPost: boolean
  mayOpenPayment: boolean
  converting: boolean
  onConvert: (lineId: string) => void
}) {
  const { t } = useApp()
  const sumIn = rows.filter((row) => row.direction === 'incoming').reduce((sum, row) => sum + amountOf(row.amount), 0)
  const sumOut = rows.filter((row) => row.direction === 'outgoing').reduce((sum, row) => sum + amountOf(row.amount), 0)
  // Сумма по валютам не складывается: при смешанных валютах итог не показывается вовсе.
  const currencies = new Set(rows.map((row) => row.currency))
  const single = currencies.size === 1 ? rows[0]?.currency : null

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {columnTitles(t).map((title, index) => (
              <th key={index} className={index === 4 || index === 5 ? 'num' : undefined}>
                {index === 7 ? <span className="sr-only">{t('payments.recon.col.action')}</span> : title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="muted">{dateText(row.date)}</td>
              <td>
                {row.kind === 'payment' && mayOpenPayment && row.number ? (
                  <Link to={`/payments/${row.id}`} className="mono">{row.number}</Link>
                ) : (
                  <span className="mono">{row.number ?? '—'}</span>
                )}
              </td>
              <td className="wrap pay-cp">
                <span className="stack">
                  <span className="secondary">{row.name ?? '—'}</span>
                  {row.kind === 'payment' ? (
                    <span className="flag flag-warning t-xs">
                      <Icon name="alert" size="xs" />
                      {row.inn
                        ? t('payments.recon.notFoundByInn', { inn: row.inn })
                        : t('payments.recon.noInn')}
                    </span>
                  ) : (
                    row.inn && <span className="t-xs muted mono">{t('column.inn')} {row.inn}</span>
                  )}
                </span>
              </td>
              <td>
                <span className="ellipsis pay-purpose" title={row.purpose ?? undefined}>{row.purpose ?? '—'}</span>
              </td>
              <td className="num">
                {row.direction === 'incoming' ? <Money value={row.amount} currency={row.currency} /> : ''}
              </td>
              <td className="num">
                {row.direction === 'outgoing' ? <Money value={row.amount} currency={row.currency} /> : ''}
              </td>
              <td className="wrap pay-status">
                <span className="status-cell">
                  {row.kind === 'line' ? (
                    <>
                      <span className="badge b-info"><Icon name="clock" />{t('payments.recon.status.line')}</span>
                      <span className="d">{t('payments.recon.status.line.detail')}</span>
                    </>
                  ) : (
                    <>
                      <span className="badge b-warning"><Icon name="alert" />{t('payments.recon.status.attention')}</span>
                      <span className="d is-warn">
                        {t('payments.recon.status.attention.detail')}
                        {row.unallocated && amountOf(row.unallocated) > 0 && (
                          <> · {t('payments.column.unallocated')} <Money value={row.unallocated} currency={row.currency} /></>
                        )}
                      </span>
                    </>
                  )}
                </span>
              </td>
              <td className="num">
                {row.kind === 'line' && mayPost && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onConvert(row.id)}
                    disabled={converting}
                  >
                    {t('payments.convertLine')}
                  </button>
                )}
                {row.kind === 'payment' && mayOpenPayment && (
                  <Link to={`/payments/${row.id}`} className="btn btn-secondary btn-sm">
                    {t('payments.recon.open')}
                  </Link>
                )}
              </td>
            </tr>
          ))}
          <tr className="total">
            <td />
            <td colSpan={3}>
              {t(filtered ? 'payments.recon.total.filtered' : 'payments.recon.total', { count: rows.length })}
            </td>
            <td className="num">{single && sumIn ? <Money value={sumIn} currency={single} /> : '—'}</td>
            <td className="num">{single && sumOut ? <Money value={sumOut} currency={single} /> : '—'}</td>
            <td colSpan={2} />
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/**
 * Содержимое файла текстом.
 *
 * `File.text()` есть не везде — в jsdom его нет вовсе, а в старых Safari он появился
 * позже `FileReader`. Падение здесь выглядит как «кнопка загрузки не работает», причём
 * без единого сообщения: исключение уходит в обработчик события, а не в состояние
 * мутации. Запасной путь через `FileReader` дешевле, чем выяснять это по жалобе.
 */
function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('Файл не прочитан'))
    reader.readAsText(file, 'utf-8')
  })
}

/**
 * Итог проведения.
 *
 * Остаток очереди показывается всегда, даже нулевой: сообщение, называющее одни успехи,
 * читается как «готово», и непроведённые строки находит потом не тот человек. Остаток
 * приходит с сервера (`stillUnprocessed`), а не считается экраном.
 */
function ConversionSummary({ result }: { result: ConversionResult }) {
  const { t } = useApp()
  const clean = result.stillUnprocessed === 0 && result.failed.length === 0
  return (
    <div className={clean ? 'alert alert-success' : 'alert alert-warning'} role="status">
      <Icon name={clean ? 'check' : 'alert'} />
      <span className="stack">
        <span className="strong">
          {t('payments.convert.done', {
            converted: result.converted,
            remaining: result.stillUnprocessed,
          })}
        </span>
        {result.failed.length > 0 && (
          <span className="fg">{t('payments.convert.failed', { ids: result.failed.join(', ') })}</span>
        )}
      </span>
    </div>
  )
}
